import { Heading } from '@finviz/website'
import React from 'react'

import { Theme } from '../../../../types/shared'
import { ChartEventBadgeType, ChartEventLabels } from '../../../constants/common'
import { BigNumber } from '../../../helpers/math'
import { CHART_EVENT_BADGE_SIZE } from '../../../models/constants'
import {
  ChartEventBadgeState,
  IEarnings,
  getFormattedEventDate,
  getFormattedValue,
} from '../../../utils/chart-events-utils'
import { encodeQueryString } from '../../../utils/query_string'
import { getChartEventBadgeElement } from '../badges/get-chart-event-badge-element'
import { ChartEventPopoverTableRow, PopoverTableRowData } from './chart-event-popover-table-row'

const SURPRISE_POSITIVE_COLOR = 'text-green-500 dark:text-green-400'
const SURPRISE_NEGATIVE_COLOR = 'text-red-500 dark:text-red-400'

function getFormattedFiscalPeriod(fiscalPeriodRaw: string) {
  const fiscalPeriod = fiscalPeriodRaw.toLowerCase()
  if (fiscalPeriod.includes('q')) {
    const [year, quarter] = fiscalPeriod.split('q')
    return `${year} Q${quarter}`
  }
  if (fiscalPeriod.includes('s')) {
    const [year, semester] = fiscalPeriod.split('s')
    return `${year} S${semester}`
  }
  return fiscalPeriodRaw
}

function getPercentValueString(value: number, totalValue: number) {
  if (totalValue === 0) {
    return ''
  }
  const percent = (value / totalValue) * 100
  const percentValue = isNaN(percent) ? 0 : Math.abs(percent)

  return `(${getFormattedValue({
    value: percentValue,
    maxDecimalPlaces: 2,
  })}%)`
}

export function ChartEventEarningsPopoverContent({
  badgeType,
  rawData,
  theme,
}: {
  badgeType: ChartEventBadgeType
  rawData: IEarnings
  theme: Theme
}) {
  const isPremium = window.FinvizSettings.hasUserPremium
  const data = React.useMemo(() => {
    const dataArray: PopoverTableRowData[] = [
      [
        { value: 'Earnings Date' },
        {
          value: getFormattedEventDate(rawData.dateTimestamp, true),
        },
      ],
      [
        { value: 'Fiscal Period' },
        {
          value: getFormattedFiscalPeriod(rawData.fiscalPeriod),
        },
      ],
      [
        { value: 'Period Ending' },
        {
          value: getFormattedEventDate(rawData.fiscalEndDate, false, {
            month: 'short',
            day: undefined,
          }),
        },
      ],
    ]

    if (rawData.epsActual !== undefined || rawData.epsEstimate !== undefined) {
      dataArray.push([{ value: 'EPS', className: 'font-medium pt-4' }])
    }
    if (rawData.epsActual !== undefined) {
      dataArray.push([{ value: 'Reported' }, { value: getFormattedValue({ value: rawData.epsActual }) }])
    }
    if (rawData.epsEstimate !== undefined) {
      dataArray.push([{ value: 'Estimate' }, { value: getFormattedValue({ value: rawData.epsEstimate }) }])
    }
    if (rawData.epsActual !== undefined && rawData.epsEstimate !== undefined) {
      const epsSurprise = rawData.epsActual - rawData.epsEstimate
      const epsSurpriseColor = epsSurprise < 0 ? SURPRISE_NEGATIVE_COLOR : SURPRISE_POSITIVE_COLOR
      const percentageValue = getPercentValueString(epsSurprise, rawData.epsEstimate)

      dataArray.push([
        { value: 'Surprise', className: epsSurpriseColor },
        {
          value: `${getFormattedValue({ value: epsSurprise })} ${percentageValue}`,
          className: epsSurpriseColor,
        },
      ])
    }

    if (rawData.epsReportedActual !== undefined && rawData.epsActual !== rawData.epsReportedActual) {
      dataArray.push([{ value: 'EPS (GAAP)', className: 'font-medium pt-4' }])

      if (isPremium) {
        dataArray.push([{ value: 'Reported' }, { value: getFormattedValue({ value: rawData.epsReportedActual }) }])

        if (rawData.epsReportedEstimate !== undefined) {
          const epsReportedSurprise = rawData.epsReportedActual - rawData.epsReportedEstimate
          const epsReportedSurpriseColor = epsReportedSurprise < 0 ? SURPRISE_NEGATIVE_COLOR : SURPRISE_POSITIVE_COLOR
          const percentageValue = getPercentValueString(epsReportedSurprise, rawData.epsReportedEstimate)

          dataArray.push(
            [{ value: 'Estimate' }, { value: getFormattedValue({ value: rawData.epsReportedEstimate }) }],
            [
              { value: 'Surprise', className: epsReportedSurpriseColor },
              {
                value: `${getFormattedValue({ value: epsReportedSurprise })} ${percentageValue}`,
                className: epsReportedSurpriseColor,
              },
            ]
          )
        }
      } else {
        dataArray.push([
          {
            value: 'Finviz*Elite only',
            className: 'text-gray-500 dark:text-gray-300 hover:underline hover:text-gray-600 dark:hover:text-gray-200',
            colSpan: 2,
            href: `/elite.ashx?${encodeQueryString({
              utm_source: 'finviz',
              utm_medium: 'banner',
              utm_campaign: 'chart-event-earnings-gaap',
            })}`,
          },
        ])
      }
    }

    if (rawData.salesActual !== undefined || rawData.salesEstimate !== undefined) {
      dataArray.push([{ value: 'Revenue', className: 'font-medium pt-4' }])
    }
    if (rawData.salesActual !== undefined) {
      dataArray.push([
        { value: 'Reported' },
        { value: getFormattedValue({ value: rawData.salesActual, bigNumberUnit: BigNumber.Million }) },
      ])
    }
    if (rawData.salesEstimate !== undefined) {
      dataArray.push([
        { value: 'Estimate' },
        { value: getFormattedValue({ value: rawData.salesEstimate, bigNumberUnit: BigNumber.Million }) },
      ])
    }
    if (rawData.salesActual !== undefined && rawData.salesEstimate !== undefined) {
      const revenueSurprise = rawData.salesActual - rawData.salesEstimate
      const revenueSurpriseColor = revenueSurprise < 0 ? SURPRISE_NEGATIVE_COLOR : SURPRISE_POSITIVE_COLOR
      const percentageValue = getPercentValueString(revenueSurprise, rawData.salesEstimate)

      dataArray.push([
        { value: 'Surprise', className: revenueSurpriseColor },
        {
          value: `${getFormattedValue({
            value: revenueSurprise,
            bigNumberUnit: BigNumber.Million,
          })} ${percentageValue}`,
          className: revenueSurpriseColor,
        },
      ])
    }

    return dataArray
  }, [rawData, isPremium])

  return (
    <>
      <div className="mb-3 flex space-x-1.5">
        {getChartEventBadgeElement({
          theme: theme,
          state: ChartEventBadgeState.default,
          width: CHART_EVENT_BADGE_SIZE,
          badgeType,
        })}{' '}
        <Heading level={4} className="my-0 text-base">
          {ChartEventLabels[badgeType]}
        </Heading>
      </div>
      <table>
        <tbody>
          {data.map((item, index) => (
            <ChartEventPopoverTableRow data={item} key={`${index}-${item[0]}`} />
          ))}
        </tbody>
      </table>
    </>
  )
}
